import {useEffect, useRef} from "react";
import {getDateFormat, getDateLong} from "../../../../../Apis/functions";
import SockJS from "sockjs-client";
import {guardURL} from "../../../../../Apis/Constants";
import {Client} from "@stomp/stompjs";
import {DEFAULT_CONTRACTOR_PATH} from "../Model/Model";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 * Created At: 26 de noviembre de 2025
 *
 * Custom hook that manages a WebSocket connection for receiving real-time contractor,
 * employee, and equipment data updates. Automatically reconnects when needed and
 * clears all previous subscriptions on reconnection.
 *
 * @param {Object} params
 * @param {Object} params.item - Selected contractor item containing date and optional contractorId.
 * @param {boolean} params.isOnline - Current WebSocket connection status.
 * @param {Function} params.setIsOnline - Setter to update connection status.
 * @param {Function} params.setActiveContractors - Setter for active contractors list.
 * @param {Function} params.setEmployeeInside - Setter for employees inside list.
 * @param {Function} params.setEmployeeOutside - Setter for employees outside list.
 * @param {Function} params.setEquipmentInside - Setter for equipment/material inside list.
 * @param {Function} params.setEquipmentOutside - Setter for equipment/material outside list.
 */
export const UseWebSocket = ({
                                 item,
                                 isOnline,
                                 setIsOnline,
                                 setActiveContractorsOptions,
                                 setActiveContractors,
                                 setEmployeeInside,
                                 setEmployeeOutside,
                                 setEquipmentInside,
                                 setEquipmentOutside,
                             }) => {

    const clientRef = useRef(null);
    const subscriptionsRef = useRef([]);

    /**
     * Establishes a WebSocket connection using STOMP protocol.
     * Subscribes to the contractor topic and publishes an initial request.
     *
     * @returns {void}
     */
    const connectWebSocket = () => {
        if (!item?.date) {
            setIsOnline(false);
            return;
        }
        const date =  getDateLong(item?.date);
        const topicDate = getDateFormat(item?.date, false, "YYYY-MM-DD");

        const socket = () => new SockJS(guardURL);
        const client = new Client({
            webSocketFactory: socket,
            debug: () => {},
            reconnectDelay: 5000,
            onConnect: () => {
                setIsOnline(true);
                subscriptionsRef.current.forEach(sub => sub.unsubscribe());
                subscriptionsRef.current = [];
                clear();

                const save = client.subscribe(`/topic/incoming_contractors/${DEFAULT_CONTRACTOR_PATH}/${topicDate}`, message => {
                    const { data } = JSON.parse(message.body);
                    if (data) {
                        handleSetData(data);
                    }
                });
                subscriptionsRef.current = [save];
                client.publish({
                    destination: `/app/incoming-contractor`,
                    body: date
                });
            },
            onStompError: (frame) => {
                setIsOnline(false);
            },
            onWebSocketClose: () => {
                setIsOnline(false);
            },
            onWebSocketError: () => {
                setIsOnline(false);
            },
        });
        client.activate();
        clientRef.current = client;
    }

    /**
     * Effect that initializes or cleans up the WebSocket connection
     * depending on changes to the selected item.
     */
    useEffect(() => {
        const date = item?.date;

        clear();

        if (date) {
            connectWebSocket(date);
        }
        return () => {
            subscriptionsRef.current.forEach(sub => sub.unsubscribe());
            subscriptionsRef.current = [];
            if (clientRef.current) {
                clientRef.current.deactivate();
                clientRef.current = null;
            }
        };
    }, [item]);

    /**
     * Filters and sets real-time WebSocket data into the state according
     * to the contractorId (if provided).
     *
     * @param {Object} data - Data payload from WebSocket.
     * @returns {void}
     */
    const handleSetData = (data) => {
        const contractorId = item?.contractorId;

        const filterByContractor = (list = []) => {
            if (!contractorId) return list;
            return list.filter(inc => inc?.contractorId === contractorId);
        };

        setActiveContractorsOptions(data?.activeContractors);
        setActiveContractors(filterByContractor(data?.activeContractors)?.map(e => ({...e, id: e?.contractorId + e?.folio})));//Map to map id to CustomExpandableTable
        setEmployeeInside(filterByContractor(data?.employeesInside));
        setEmployeeOutside(filterByContractor(data?.employeesOutside));
        setEquipmentInside(filterByContractor(data?.equipmentInside));
        setEquipmentOutside(filterByContractor(data?.equipmentOutside));
    };



    /**
     * Attempts to reconnect the WebSocket when offline.
     *
     * @returns {void}
     */
    const reconnectWebSocket = () => {
        if (!isOnline && !clientRef.current?.connected) {
            connectWebSocket();
        }
    };

    const clear = () => {
        setActiveContractorsOptions([]);
        setActiveContractors([]);
        setEmployeeInside([]);
        setEmployeeOutside([]);
        setEquipmentInside([]);
        setEquipmentOutside([]);
    }

    return {reconnectWebSocket};
}
